import { inject, Injectable } from '@angular/core';
import { combineLatest, fromEvent, mergeMap, Observable, of, shareReplay, switchMap } from 'rxjs';
import { catchError, debounceTime, filter, map, take, tap } from 'rxjs/operators';
import { BazisSrvService } from '@bazis/shared/services/srv.service';
import { BazisModalService } from '@bazis/shared/services/modal.service';
import { EntData, EntDocumentSettings } from '@bazis/shared/models/srv.types';
import { TemplateObservable } from '@bazis/shared/classes/template-observable';
import { CryptoSigningComponent } from '@bazis/signature/crypto/components/signing/signing.component';
import { BazisEntityService } from '@bazis/shared/services/entity.service';
import { HttpClient } from '@angular/common/http';
import { BazisConfigurationService, SHARE_REPLAY_SETTINGS } from '@bazis/configuration.service';
import moment from 'moment';
import { DocEntData, DocListItem } from '@bazis/shared/models/document.types';
import { DocusignSigningComponent } from '@bazis/signature/docusign/components/signing/signing.component';
import { NCALayerSigningComponent } from '@bazis/signature/ncalayer/components/signing/signing.component';

/**
 * **BazisDocumentService**
 *
 * Service for document signing flows.
 * Builds signing settings lists, opens signing modals, calls backend signing endpoints,
 * and refreshes related entities/documents after signing.
 */
@Injectable({
    providedIn: 'root',
})
export class BazisDocumentService {
    private srv = inject(BazisSrvService);

    private http = inject(HttpClient);

    private modalService = inject(BazisModalService);

    private entityService = inject(BazisEntityService);

    private configurationService = inject(BazisConfigurationService);

    /**
     * Mapping of signature type to a component that performs signing UI flow.
     * Used by `openModalToSignDocuments()` when `signingComponent` is not provided.
     */
    signingComponents = {
        crypto: CryptoSigningComponent,
        docusign: DocusignSigningComponent,
        NCALayer: NCALayerSigningComponent,
    };

    /**
     * Modal type labels used by `BazisModalService` for each signature type.
     */
    modalType = {
        crypto: 'signing',
        docusign: 'docusign signing',
        NCALayer: 'signing',
    };

    /**
     * @param srv - Backend API client for document/sign endpoints.
     * @param http - Angular `HttpClient` used to download document blobs.
     * @param modalService - Modal service used to open signing UI and dismiss on errors.
     * @param entityService - Entity API client used to refresh signed entities/documents.
     * @param configurationService - Configuration source that provides `signatureType`.
     */
    /**
     * Create an observable list of document settings and open a signing modal for them.
     *
     * The returned Observable emits the current settings list value stored inside `TemplateObservable`.
     * When signing is cancelled or fails, the service sets settings to `null`.
     *
     * @param settings - Initial list of signing settings.
     * @param forceLoadEntitiesAfterSigning - If true, reload related entities after signing. Default: true.
     * @param signingComponent - Optional custom signing component. Default: `null` (auto-select by config).
     * @returns Observable of settings list (`EntDocumentSettings[]`) as it changes during flow.
     */
    createGroupToSignBySettings(
        settings: EntDocumentSettings[],
        forceLoadEntitiesAfterSigning = true,
        signingComponent = null,
    ): Observable<EntDocumentSettings[]> {
        const documentSettings = new TemplateObservable(settings);
        this.openModalToSignDocuments(
            documentSettings,
            signingComponent,
            forceLoadEntitiesAfterSigning,
        );
        return documentSettings.$;
    }

    /**
     * Fetch a signable document for an entity.
     *
     * When `cancelPreviousSignings` is true:
     * - Cancels previous signings on backend
     * - Forces entity reload
     * - Fetches document again
     *
     * @param entityType - Entity type string.
     * @param entityId - Entity id string.
     * @param contextLabel - Optional signing context label (backend-specific). Default: `null`.
     * @param documentPayload - Extra payload sent to backend when fetching document. Default: `{}`.
     * @param cancelPreviousSignings - If true, cancels previous signings first. Default: false.
     * @returns Observable of fetched document entity/data (`EntData`).
     */
    getSignDocument$(
        entityType: string,
        entityId: string,
        contextLabel: string = null,
        documentPayload: any = {},
        cancelPreviousSignings: boolean = false,
    ): Observable<EntData> {
        return cancelPreviousSignings
            ? this.srv.cancelEntityDocumentSignings$(entityType, entityId, contextLabel).pipe(
                  switchMap(() =>
                      this.entityService.getEntity$(entityType, entityId, { forceLoad: true }),
                  ),
                  switchMap(() =>
                      this.srv.fetchEntityDocument$(
                          entityType,
                          entityId,
                          contextLabel,
                          documentPayload,
                      ),
                  ),
              )
            : this.srv.fetchEntityDocument$(entityType, entityId, contextLabel, documentPayload);
    }

    /**
     * Sign a single entity document and refresh the entity from API.
     *
     * Reload includes `documents` relationship unless `settings.withoutIncludedDocuments` is true.
     *
     * @param settings - Document signing settings for one entity.
     * @param signature - Signature value produced by signing component/provider.
     * @param signBodyPayload - Optional additional payload for sign request. Default: `null`.
     * @returns Observable of refreshed signed entity (`EntData`), completes after first value.
     */
    signEntityDocument$(settings: EntDocumentSettings, signature, signBodyPayload = null) {
        return this.srv.signEntity$(settings, signature, signBodyPayload).pipe(
            mergeMap(() =>
                this.entityService.getEntity$(settings.entityType, settings.entityId, {
                    forceLoad: true,
                    include: settings.withoutIncludedDocuments ? [] : ['documents'],
                }),
            ),
            take(1),
        );
    }

    /**
     * Sign multiple entity documents in one request and optionally refresh entities after signing.
     *
     * On error, dismisses modal and rethrows.
     *
     * @param signSettings - Array of settings objects: `{ settings, signature, signBodyPayload }`.
     * @param forceLoadEntitiesAfterSigning - If true, refresh entities after signing. Default: true.
     * @returns Observable that emits refreshed entities array (or empty array when refresh disabled).
     */
    signEntityDocuments$(
        signSettings: { settings: EntDocumentSettings; signature; signBodyPayload }[],
        forceLoadEntitiesAfterSigning = true,
    ) {
        return this.srv.signEntities$(signSettings).pipe(
            mergeMap(() =>
                this.updateSignedEntities$(
                    signSettings.map((v) => v.settings),
                    forceLoadEntitiesAfterSigning,
                ),
            ),
            catchError((e) => {
                this.modalService.dismiss();
                throw e;
            }),
        );
    }

    /**
     * Reload entities that were signed, without duplicates.
     *
     * When `forceLoadEntitiesAfterSigning` is false or there are no settings, returns `of([])`.
     *
     * @param signSettings - List of signed document settings.
     * @param forceLoadEntitiesAfterSigning - Reload flag.
     * @returns Observable of refreshed entities array.
     */
    updateSignedEntities$(signSettings: EntDocumentSettings[], forceLoadEntitiesAfterSigning) {
        if (!forceLoadEntitiesAfterSigning || signSettings.length === 0) return of([]);
        const entities = [];
        signSettings.forEach((setting) => {
            if (
                !entities.find(
                    (entity) =>
                        entity.entityType === setting.entityType &&
                        entity.entityId === setting.entityId,
                )
            ) {
                entities.push(setting);
            }
        });
        return combineLatest([
            ...entities.map((entity) =>
                this.entityService.getEntity$(entity.entityType, entity.entityId, {
                    forceLoad: true,
                    include: entity.withoutIncludedDocuments ? [] : ['documents'],
                }),
            ),
        ]).pipe(filter((items) => items.filter((v) => !!v).length === items.length));
    }

    /**
     * Open a modal dialog with signing component and handle modal dismissal.
     *
     * When modal is dismissed without response, sets `settings` to `null`.
     * When response includes `signed` map, filters out already signed items and updates settings.
     *
     * @param settings - Settings holder that contains items to sign.
     * @param signingComponent - Optional component class to use. Default: `null` (auto-select by config).
     * @param forceLoadEntitiesAfterSigning - If true, signing component should refresh entities. Default: true.
     * @returns void
     */
    protected openModalToSignDocuments(
        settings: TemplateObservable<EntDocumentSettings[]>,
        signingComponent = null,
        forceLoadEntitiesAfterSigning = true,
    ) {
        if (!signingComponent) {
            signingComponent = this.signingComponents[this.configurationService.signatureType];
        }
        const modal = this.modalService.create({
            component: signingComponent,
            componentProperties: {
                itemsToSign: settings,
                forceLoadEntitiesAfterSigning,
            },
            modalType: this.modalType[this.configurationService.signatureType],
        });

        modal
            .onDidDismiss()
            .then((response) => {
                if (!response) {
                    settings.set(null);
                    return null;
                }
                const remainToSign = response.signed
                    ? settings._.filter(
                          (entity) =>
                              !response.signed[
                                  `${entity.entityType}-${entity.entityId}-${
                                      entity.contextLabel || ''
                                  }`
                              ],
                      )
                    : settings._;
                //settings.set(remainToSign);
                settings.set(remainToSign.length > 0 ? null : remainToSign);
                return response;
            })
            .catch((error) => {
                console.log(error);
                settings.set(null);
            });
    }

    /**
     * Download file content and return base64 content without data URL header.
     *
     * This is typically used by signing providers that require raw base64 file content.
     *
     * @param url - File URL to download.
     * @returns Observable that emits base64 string content (without `data:*;base64,` prefix).
     */
    getFileContentToSign$(url) {
        return this.http
            .get(url, {
                responseType: 'blob',
            })
            .pipe(
                mergeMap((blob: any) => {
                    const reader = new FileReader();
                    reader.readAsDataURL(blob);
                    return fromEvent(reader, 'load').pipe(
                        map((event) => {
                            let header = ';base64,';
                            let sFileData = reader.result as string;
                            return sFileData.substr(sFileData.indexOf(header) + header.length);
                        }),
                        take(1),
                    );
                }),
            );
    }

    /**
     * Create a map of sign documents by document id.
     *
     * The result keeps original document object and clones `attributes` to avoid mutations.
     *
     * @param signDocuments - Array of sign document objects.
     * @returns Map `{ [documentId]: signDocument }`.
     */
    static generateSignDocumentsMap(signDocuments: any[]) {
        const result = {};
        signDocuments.forEach((document) => {
            result[document.id] = {
                ...document,
                attributes: {
                    ...document.attributes,
                },
            };
        });
        return result;
    }

    /**
     * Filter documents list to only documents that are available for signing.
     *
     * A document is available when it exists in `signDocuments` map and has non-empty `signatories`.
     *
     * @param documents - List of document entities.
     * @param signDocuments - Sign documents array (backend-provided structure).
     * @returns Filtered documents array.
     */
    static generateAvailiableDocuments(documents: EntData[], signDocuments: any[]) {
        const signTargetsMap = BazisDocumentService.generateSignDocumentsMap(signDocuments);
        return documents.filter(
            (document) =>
                signTargetsMap[document.$snapshot.id] &&
                signTargetsMap[document.$snapshot.id].attributes.signatories.length > 0,
        );
    }

    /**
     * Build an observable list of document entities for a given parent entity.
     *
     * When `doNotInitDocLoad` is false (default), waits until all documents are loaded (non-null).
     * When true, returns loaded subset and does not enforce full load.
     *
     * @param entity$ - Observable that emits a parent entity.
     * @param doNotInitDocLoad - If true, do not initiate/await full document load. Default: false.
     * @returns Observable of document entities array.
     */
    getDocList$(entity$: Observable<EntData>, doNotInitDocLoad = false): Observable<EntData[]> {
        return entity$.pipe(
            filter((v) => !!v),
            switchMap((entity) => {
                return entity.$snapshot.documents.length > 0
                    ? combineLatest(
                          entity.$snapshot.documents.map((doc) =>
                              this.entityService.getEntity$(doc.type, doc.id, {
                                  doNotInitLoad: doNotInitDocLoad,
                              }),
                          ),
                      ).pipe(
                          debounceTime(0),
                          filter(
                              (docs: EntData[]) =>
                                  doNotInitDocLoad ||
                                  docs.length === docs.filter((v) => !!v).length,
                          ),
                          map((docs) => docs.filter((doc) => !!doc)),
                          shareReplay(SHARE_REPLAY_SETTINGS),
                      )
                    : of([]);
            }),
            map((listResult) => {
                return listResult || [];
            }),
            shareReplay(SHARE_REPLAY_SETTINGS),
        );
    }

    /**
     * Normalize sign documents array from entity snapshot.
     *
     * When signature type is `docusign`, uses `envelope_items` as signatories list.
     *
     * @param entity - Entity with `$snapshot.sign_documents` field.
     * @returns Normalized sign documents array.
     */
    getSignDocuments(entity) {
        const signDocuments = entity ? entity.$snapshot.sign_documents : [];

        return signDocuments.map((v) => {
            let signatories = v.attributes.signatories;

            if (this.configurationService.signatureType === 'docusign') {
                signatories = [...v.attributes.envelope_items];
            }

            return {
                ...v,
                attributes: {
                    ...v.attributes,
                    signatories,
                },
            };
        });
    }

    /**
     * Observable wrapper for `getSignDocuments()`.
     *
     * @param entity$ - Observable of entity.
     * @returns Observable of sign documents array.
     */
    protected getSignDocuments$(entity$: Observable<EntData>): Observable<any[]> {
        return entity$.pipe(
            map((entity) => this.getSignDocuments(entity)),
            shareReplay(SHARE_REPLAY_SETTINGS),
        );
    }

    /**
     * Build document list view model for an entity.
     *
     * Combines:
     * - documents loaded from `getDocList$()`
     * - sign documents info from `getSignDocuments$()`
     *
     * Produces list items with:
     * - `displayDocument`: main document to show
     * - `popupDocument`: original non-digital document entity
     * - `signInfo`: sign document info with signatories
     *
     * @param entity$ - Observable of parent entity.
     * @param doNotInitDocLoad - Pass-through flag for `getDocList$()`. Default: false.
     * @returns Observable of `DocListItem[]`.
     */
    getEntityDocuments$(
        entity$: Observable<EntData>,
        doNotInitDocLoad = false,
    ): Observable<DocListItem[]> {
        const list$: Observable<EntData[]> = this.getDocList$(entity$, doNotInitDocLoad);
        const signDocs$: Observable<EntData[]> = this.getSignDocuments$(entity$);
        return combineLatest([list$, signDocs$]).pipe(
            map(([documentList, signDocs]) => {
                const signMap = (signDocs || []).reduce(
                    (acc, current) => ({ ...acc, [current.id]: current }),
                    {},
                );
                documentList = documentList
                    .map(
                        (v): DocEntData => ({
                            ...v,
                            $timestamp: moment(v.$snapshot.dt_created).valueOf(),
                        }),
                    )
                    .sort((a, b) => b.$timestamp - a.$timestamp);

                const notDigitalDocs = documentList.filter((v) => !v.$snapshot.is_digital);

                return notDigitalDocs
                    .map((notDigitalDoc: EntData) => {
                        const popupDocument = notDigitalDoc;
                        const displayDocument =
                            documentList.find((v) => v.$snapshot.origin_id === notDigitalDoc.id) ||
                            popupDocument;
                        const signInfo = signMap[popupDocument.id]
                            ? {
                                  id: signMap[popupDocument.id].id,
                                  type: signMap[popupDocument.id].type,
                                  $snapshot: signMap[popupDocument.id].attributes,
                              }
                            : null;
                        return { displayDocument, popupDocument, signInfo };
                    })
                    .filter(
                        (v) =>
                            v.displayDocument &&
                            v.signInfo &&
                            v.signInfo.$snapshot.signatories.length > 0,
                    )
                    .sort((a, b) => b.displayDocument.$timestamp - a.displayDocument.$timestamp);
            }),
            shareReplay(SHARE_REPLAY_SETTINGS),
        );
    }
}
